import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("jc_user") || "null");
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);
  // PO 2026-02 · Trial Paywall V1. `entitlement === "expired"` is set
  // when the backend paywall middleware returns 402 on any gated
  // request. Cleared on login/logout so a fresh session starts clean.
  const [entitlement, setEntitlement] = useState(null);

  useEffect(() => {
    // Global 402 listener — the api.js interceptor emits a
    // custom event whenever the paywall middleware blocks a call.
    const on_paywall = (event) => {
      const reason = event?.detail?.entitlement;
      setEntitlement(reason === "checkout_required" ? "checkout_required" : "expired");
    };
    window.addEventListener("jc:paywall_expired", on_paywall);
    return () => window.removeEventListener("jc:paywall_expired", on_paywall);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("jc_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("jc_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("jc_token");
        localStorage.removeItem("jc_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("jc_token", data.token);
    localStorage.setItem("jc_user", JSON.stringify(data.user));
    setEntitlement(null);
    // LEVI-FIRST-01 · Session-scoped marker for a meaningful fresh
    // authenticated entry. Consumed exactly once by the Levi-primary
    // dashboard presentation and then removed so refresh/navigation
    // do not replay it. Any prior manual-mode choice from a previous
    // session must not leak into a brand-new login.
    try {
      sessionStorage.setItem("jc:levi:fresh_login", "1");
      sessionStorage.removeItem("jc:levi:manual_mode");
    } catch { /* sessionStorage may be unavailable */ }
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("jc_token", data.token);
    localStorage.setItem("jc_user", JSON.stringify(data.user));
    setEntitlement(null);
    try {
      sessionStorage.setItem("jc:levi:fresh_login", "1");
      sessionStorage.removeItem("jc:levi:manual_mode");
    } catch { /* sessionStorage may be unavailable */ }
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("jc_token");
    localStorage.removeItem("jc_user");
    // PO QA authorization (2026-07-30) · Phase 5C Memory Authority.
    // Clear the persisted Levi working session on logout so
    // temporary operational state (pending_action, resolved_context,
    // pending_objectives, turns[]) does not silently resume across
    // an authentication boundary. Business data / project history
    // are unaffected — they live in server-side records the next
    // login will re-resolve from scratch.
    try {
      localStorage.removeItem("jc.levi.session_id");
    } catch { /* ignore */ }
    // Clear session-scoped Levi flags so a new authenticated session
    // starts clean.
    try {
      sessionStorage.removeItem("jc:levi:fresh_login");
      sessionStorage.removeItem("jc:levi:manual_mode");
    } catch { /* ignore */ }
    setUser(null);
    window.location.href = "/";
  };

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("jc_user", JSON.stringify(data));
      return data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshMe, entitlement, setEntitlement }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
